// @flow
import { hierarchy as d3hierarchy } from 'd3-hierarchy';
import d3tree from 'd3-flextree-v4';
import {
  ENTRY_TYPE,
  MESSAGE_ACTION_TYPE,
  DELAY_TYPE,
  EVENT_LISTENER_TYPE,
  MESSAGE_TYPE,
  EXIT_TYPE,
  ADD_NODE_TYPE,
  LABEL_NODE_TYPE
} from '../constants/ApiTypes';

import {
  LABEL_HEIGHT,
  LABEL_WIDTH,
  LABEL_RADIUS_X,
  LABEL_RADIUS_Y,
  LABEL_FILL
} from '../components/LabelNode';

import {
  STANDARD_HEIGHT,
  STANDARD_WIDTH,
  STANDARD_RADIUS_X,
  STANDARD_RADIUS_Y,
  STANDARD_FILL
} from '../components/StandardNode';

import { STANDARD_ADD_NODE_SIZE } from '../components/AddNode';

type Node = {
  node_type: string,
  next: string
};

type NodeData = {
  height?: number,
  width?: number,
  rx?: number,
  ry?: number,
  fill?: string
};

let nodeId = 0;

const API_TYPES = [ENTRY_TYPE, DELAY_TYPE, MESSAGE_TYPE, EVENT_LISTENER_TYPE];

export const getParentName = (nodeData: Object = {}): string | null => {
  const { parent = { data: {} } } = nodeData;
  const { data: { node_type: nodeType = '', name = '' } } = parent;
  if (!nodeType) {
    return null;
  }
  return API_TYPES.indexOf(nodeType) > -1 ? name : getParentName(parent);
};

export const createNode = (nodeData: NodeData = {}): Object => {
  nodeId += 1;
  const {
    height = STANDARD_HEIGHT,
    width = STANDARD_WIDTH,
    rx = STANDARD_RADIUS_X,
    ry = STANDARD_RADIUS_Y,
    fill = STANDARD_FILL
  } = nodeData;
  const node = {
    id: nodeId,
    ...nodeData,
    height,
    width,
    rx,
    ry,
    fill,
    children: []
  };
  return node;
};

export const createAddNode = (nodeData: NodeData = {}): Object =>
  createNode({
    ...nodeData,
    node_type: ADD_NODE_TYPE,
    height: STANDARD_ADD_NODE_SIZE,
    width: STANDARD_ADD_NODE_SIZE
  });

export const undecoratePath = (
  parentNode: Object = { children: [] },
  children: Array<NodeData> = []
) => {
  parentNode.children.push(children);
};

export const decoratePath = (
  parentNode: {
    children: Array<NodeData>,
    name: string
  } = { children: [], name: '' },
  children: Array<NodeData> = [],
  nodeData: { nextKey: string } = { nextKey: '' }
) => {
  const addNode = createAddNode({
    ...nodeData,
    name: `add-${nodeData.nextKey}-${parentNode.name}`
  });
  addNode.children.push(children);
  parentNode.children.push(addNode);
};

export const createLabelNode = (
  nodeData: {
    nextKey: string,
    next: string,
    parentName: string,
    labelText: string,
    name: string
  } = {
    nextKey: '',
    next: '',
    parentName: '',
    labelText: '',
    name: ''
  }
): Object =>
  createNode({
    ...nodeData,
    node_type: LABEL_NODE_TYPE,
    height: LABEL_HEIGHT,
    width: LABEL_WIDTH,
    rx: LABEL_RADIUS_X,
    ry: LABEL_RADIUS_Y,
    fill: LABEL_FILL
  });

export const keysForType = {
  [MESSAGE_TYPE]: ['on_unreachable', 'on_sent'],
  [EVENT_LISTENER_TYPE]: ['on_seen', 'on_expired']
};

export const labelsForType = {
  [MESSAGE_TYPE]: ['Unreachable', 'Sent'],
  [EVENT_LISTENER_TYPE]: ['did', 'did not']
};

export const getEntryNodeId = (
  nodeObj: { [string]: Node } = {}
): string | typeof undefined =>
  Object.keys(nodeObj).find(k => nodeObj[k].node_type === ENTRY_TYPE);

export const convertNodesToTree = (
  nodeName: string = '',
  node: Node = { node_type: '', next: '' },
  nodes: { [string]: Node } = {},
  decorate: Function = decoratePath
): Object => {
  switch (node.node_type) {
    case ENTRY_TYPE:
    case MESSAGE_ACTION_TYPE:
    case DELAY_TYPE: {
      const newNode = createNode({
        ...node,
        name: `${nodeName}`
      });

      const nextNode = {
        nextKey: 'next',
        next: node.next,
        parentName: `${nodeName}`
      };
      decorate(
        newNode,
        convertNodesToTree(
          nextNode.next,
          nodes[nextNode.next],
          nodes,
          decorate
        ),
        nextNode
      );

      return newNode;
    }
    case MESSAGE_TYPE: {
      // TODO: When we support flows for unreachable condition, use generic code for event listener node below
      const rightKey = keysForType[node.node_type][1];
      const newNode = createNode({
        ...node,
        name: `${nodeName}`
      });

      const nextNode = {
        nextKey: rightKey,
        next: node[rightKey],
        parentName: `${nodeName}`
      };

      decorate(
        newNode,
        convertNodesToTree(
          nextNode.next,
          nodes[nextNode.next],
          nodes,
          decorate
        ),
        nextNode
      );
      return newNode;
    }
    case EVENT_LISTENER_TYPE: {
      const labels = labelsForType[node.node_type];
      const newNode = createNode({
        ...node,
        name: `${nodeName}`
      });

      keysForType[node.node_type].forEach((key, idx) => {
        const nextNode = {
          nextKey: key,
          next: node[key],
          parentName: `${nodeName}`
        };

        const labelNode = createLabelNode({
          parentName: `${nodeName}`,
          name: `label-${key}-${nodeName}`,
          ...nextNode,
          labelText: labels[idx]
        });
        newNode.children.push(labelNode);

        decorate(
          labelNode,
          convertNodesToTree(
            nextNode.next,
            nodes[nextNode.next],
            nodes,
            decorate
          ),
          nextNode
        );
      });
      return newNode;
    }
    case EXIT_TYPE: {
      const exitNode = createNode({
        ...node,
        name: `${nodeName}`
      });
      return exitNode;
    }
    default: {
      return createNode({ height: STANDARD_HEIGHT, width: STANDARD_WIDTH });
    }
  }
};

export const buildTree = (
  nodes: { [string]: Node } = {},
  readOnly: boolean = false
): { nodesList: Array<Object>, linksList: Array<Object> } => {
  const entryId = getEntryNodeId(nodes);
  if (!entryId) {
    return {
      nodesList: [],
      linksList: []
    };
  }

  const treeData = readOnly
    ? convertNodesToTree(entryId, nodes[entryId], nodes, undecoratePath)
    : convertNodesToTree(entryId, nodes[entryId], nodes, decoratePath);

  const hierarchy = d3hierarchy(treeData, d => {
    const { children } = d;
    // removes a lot of duplicate data in the state
    d.children = null; //eslint-disable-line
    return children;
  });
  const root = d3tree()
    .nodeSize(node => [
      node.data.width ? node.data.width : 100,
      node.data.height ? node.data.height + 50 : 150
    ])
    .spacing(() => 75)(hierarchy);
  const nodesList = root.descendants();
  const linksList = root.links();

  return {
    nodesList,
    linksList
  };
};



// WEBPACK FOOTER //
// ./src/util/TreeUtils.js